import { useState } from 'react';
import { Link } from 'react-router-dom';
import RecipeCard from '../components/RecipeCard';
import FilterBar from '../components/FilterBar';
import EmptyState from '../components/EmptyState';
import { useFilter } from '../hooks/useFilter';
import recipes from '../data/recipes';
import styles from './BakingPage.module.css';

const DIFFICULTIES = ['All', 'Easy', 'Medium', 'Hard'];

/* baking ingredient particles */
const INGREDIENTS = ['🧈','🥚','🍫','🌾','🧂','🥛','🍯','🍋','🌿','🫐','🍓','🧁'];

const STATS = [
  { n: recipes.length,                                         l: 'Recipes',    e: '📖' },
  { n: recipes.filter(r => r.difficulty === 'Easy').length,   l: 'Easy Wins',  e: '🌟' },
  { n: new Set(recipes.map(r => r.category)).size,            l: 'Categories', e: '🗂' },
  { n: recipes.filter(r => r.featured).length,                l: 'Featured',   e: '⭐' },
];

export default function BakingPage() {
  const { filtered, search, setSearch, category, setCategory, categories } =
    useFilter(recipes, 'title', 'category');

  const [difficulty, setDifficulty] = useState('All');
  const [view, setView] = useState('grid');   // 'grid' | 'list'

  const final = difficulty === 'All'
    ? filtered
    : filtered.filter(r => r.difficulty === difficulty);

  const featured = recipes.filter(r => r.featured);
  const rest     = final.filter(r => !r.featured);

  return (
    <div className={styles.root}>

      {/* ══════════════════════════════════════
          CINEMATIC HERO
      ══════════════════════════════════════ */}
      <section className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroNoise} />

        {/* Floating ingredients */}
        <div className={styles.floatLayer} aria-hidden="true">
          {INGREDIENTS.map((ing, i) => (
            <span
              key={i}
              className={styles.floatIng}
              style={{
                left:              `${4 + (i * 8.1) % 92}%`,
                top:               `${8 + (i * 13.3) % 80}%`,
                animationDelay:    `${i * 0.45}s`,
                animationDuration: `${5 + (i % 3) * 2}s`,
                fontSize:          `${1.4 + (i % 3) * 0.6}rem`,
                opacity:           0.12 + (i % 4) * 0.06,
              }}
            >
              {ing}
            </span>
          ))}
        </div>

        <div className={`container ${styles.heroInner}`}>
          {/* Left - text */}
          <div className={styles.heroLeft}>
            <div className={styles.heroPill}>
              <span className={styles.pillDot} />
              From scratch, always
            </div>

            <h1 className={styles.heroH1}>
              The<br />
              <span className={styles.heroAccent}>Bakery</span>
            </h1>

            <p className={styles.heroSub}>
              Every recipe made at least twice - once to learn it, once to perfect it.
              No shortcuts, no life stories, just the good stuff.
            </p>

            <div className={styles.heroCtas}>
              <a href="#recipes" className={styles.ctaDark}>Browse Recipes ↓</a>
              <Link to="/" className={styles.ctaGhost}>← Back Home</Link>
            </div>
          </div>

          {/* Right - featured stack */}
          <div className={styles.heroRight}>
            <div className={styles.featStack}>
              {featured.slice(0, 3).map((r, i) => (
                <Link
                  key={r.id}
                  to={`/baking/${r.slug}`}
                  className={styles.featCard}
                  style={{
                    background:     r.color,
                    transform:      `rotate(${[-3, 1, -1.5][i]}deg) translateY(${[0, 10, 20][i]}px)`,
                    zIndex:         3 - i,
                    animationDelay: `${i * 0.15}s`,
                  }}
                >
                  <span className={styles.featEmoji}>{r.emoji}</span>
                  <span className={styles.featTitle}>{r.title}</span>
                  <span className={styles.featCat} style={{ color: r.accentColor }}>
                    {r.category} · {r.time}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Stats strip inside hero */}
        <div className={styles.statsStrip}>
          <div className="container">
            <div className={styles.statsRow}>
              {STATS.map(({ n, l, e }) => (
                <div key={l} className={styles.stat}>
                  <span className={styles.statE}>{e}</span>
                  <span className={styles.statN}>{n}</span>
                  <span className={styles.statL}>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* curved wave */}
        <div className={styles.wave}>
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,20 1440,30 L1440,80 L0,80 Z" fill="#fdf8fc"/>
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CATEGORY SHOWCASE STRIP
      ══════════════════════════════════════ */}
      <section className={styles.catShowcase}>
        <div className="container">
          <div className={styles.catStrip}>
            {categories.filter(c => c !== 'All').map(cat => {
              const example = recipes.find(r => r.category === cat);
              return (
                <button
                  key={cat}
                  className={`${styles.catChip} ${category === cat ? styles.catChipActive : ''}`}
                  onClick={() => setCategory(category === cat ? 'All' : cat)}
                >
                  <span className={styles.catChipEmoji}>{example?.emoji || '🍴'}</span>
                  <span className={styles.catChipLabel}>{cat}</span>
                  <span className={styles.catChipCount}>
                    {recipes.filter(r => r.category === cat).length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          SEARCH + FILTER TOOLBAR
      ══════════════════════════════════════ */}
      <div id="recipes" className={styles.toolbar}>
        <div className={`container ${styles.toolbarInner}`}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              className={styles.searchInput}
              placeholder="Search recipes, ingredients, tags…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.searchClear} onClick={() => setSearch('')}>✕</button>
            )}
          </div>

          <div className={styles.toolbarRight}>
            <FilterBar categories={DIFFICULTIES} active={difficulty} onChange={setDifficulty} />

            {/* View toggle */}
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`}
                onClick={() => setView('grid')}
                title="Grid view"
              >⊞</button>
              <button
                className={`${styles.viewBtn} ${view === 'list' ? styles.viewBtnActive : ''}`}
                onClick={() => setView('list')}
                title="List view"
              >≡</button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          RECIPE RESULTS
      ══════════════════════════════════════ */}
      <div className={styles.results}>
        <div className="container">

          {/* Result count */}
          {search && (
            <p className={styles.resultCount}>
              {final.length} recipe{final.length !== 1 ? 's' : ''} for
              <strong> "{search}"</strong>
            </p>
          )}

          {final.length === 0 ? (
            <EmptyState emoji="🔍" text="No recipes found. Try clearing your filters!" />
          ) : view === 'list' ? (
            /* ── LIST VIEW ── */
            <div className={styles.listView}>
              {final.map((r, i) => (
                <Link
                  key={r.id}
                  to={`/baking/${r.slug}`}
                  className={styles.listRow}
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className={styles.listEmoji} style={{ background: r.color }}>
                    {r.emoji}
                  </div>
                  <div className={styles.listBody}>
                    <div className={styles.listMeta}>
                      <span style={{ color: r.accentColor, fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                        {r.category}
                      </span>
                      <span className={styles.listDot}>·</span>
                      <span className={styles.listMetaItem}>⏱ {r.time}</span>
                      <span className={styles.listDot}>·</span>
                      <span className={styles.listMetaItem}>🍽 {r.servings} servings</span>
                    </div>
                    <h3 className={styles.listTitle}>{r.title}</h3>
                    <p className={styles.listDesc}>{r.description}</p>
                  </div>
                  <div className={styles.listRight}>
                    <span
                      className={styles.listDiff}
                      style={{ color: { Easy:'#4caf87', Medium:'#f0a030', Hard:'#e05050' }[r.difficulty] }}
                    >
                      {r.difficulty}
                    </span>
                    <span className={styles.listCta} style={{ color: r.accentColor }}>Cook →</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            /* ── GRID VIEW ── */
            <div className={styles.gridView}>
              {final.map((r, i) => (
                <RecipeCard
                  key={r.id}
                  recipe={r}
                  style={{ animationDelay: `${i * 0.07}s` }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          BAKING TIPS BAND
      ══════════════════════════════════════ */}
      <section className={styles.tipsBand}>
        <div className="container">
          <h2 className={styles.tipsTitle}>🧠 Baker's Wisdom</h2>
          <div className={styles.tipsGrid}>
            {[
              { emoji:'🧈', tip:'Always use room-temperature butter unless the recipe specifically says cold.' },
              { emoji:'⚖️', tip:'Baking is science - weigh ingredients. Cup measurements lead to inconsistency.' },
              { emoji:'🌡️', tip:'An oven thermometer is the single best upgrade you can make to your kitchen.' },
              { emoji:'⏳', tip:'Under-baked is usually salvageable. Over-baked is not. When in doubt, check early.' },
            ].map(({ emoji, tip }) => (
              <div key={tip} className={styles.tipCard}>
                <span className={styles.tipEmoji}>{emoji}</span>
                <p className={styles.tipText}>{tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
